import { isDefined, isNonEmpty, isNotDefined } from '@sgme/fp';

import { selectCurrenciesQueryResult, selectDataByCurrencyPair } from '@/api/config/selectors.ts';
import { workspaceApi } from '@/api/workspace/index.ts';
import { cashApi } from '@/store/cash/api';
import { selectCashTile } from '@/store/cash/selectors/tile.ts';
import { cashClientIdUpdated } from '@/store/cash/slice.ts';
import { initiateCheckFlexiForwardByTradeCapture } from '@/store/flexi-forward/api';
import { selectFlexiForwardTile } from '@/store/flexi-forward/selectors/tile';
import { flexiForwardClientChanged } from '@/store/flexi-forward/slice.ts';
import { forwardAccumulatorApi } from '@/store/forwardAccumulator/api/index.ts';
import { forwardAccumulatorSlice } from '@/store/forwardAccumulator/slice.ts';
import type { StartAppListening } from '@/store/listenerMiddleware.ts';
import { optionSlice } from '@/store/option/slice.ts';
import { checkOptionThunk } from '@/store/option/thunks/checkOptionThunk.ts';
import { algoOrderApi } from '@/store/orders/algoOrders/api/index.ts';
import { selectAlgoTile } from '@/store/orders/algoOrders/selectors.ts';
import { callOrderApi } from '@/store/orders/call/api/index.ts';
import { selectCallOrderTile } from '@/store/orders/call/selectors.ts';
import { fixingOrderApi } from '@/store/orders/fixing/api/index.ts';
import { selectFixingTile } from '@/store/orders/fixing/selectors.ts';
import { ordersSlice } from '@/store/orders/slice.ts';
import { stopLossOrderApi } from '@/store/orders/stopLoss/api/index.ts';
import { selectStopLossTile } from '@/store/orders/stopLoss/selectors.ts';
import { takeProfitOrderApi } from '@/store/orders/takeProfitOrder/api/index.ts';
import { selectTakeProfitTile } from '@/store/orders/takeProfitOrder/selectors.ts';
import { swapApi } from '@/store/swap/api';
import { selectSwapTile } from '@/store/swap/selectors/tile.ts';
import { swapClientIdUpdated } from '@/store/swap/slice.ts';
import { getSwapTradeCaptureQueue } from '@/store/swap/swapTradeCaptureQueue.ts';
import { onConfirmRemoveSharedTab } from '@/store/tabs/slice.ts';
import { targetAccumulatorApi } from '@/store/targetAccumulator/api/index.ts';
import { targetAccumulatorSlice } from '@/store/targetAccumulator/slice.ts';
import { selectTileClientId } from '@/store/tiles/selectors.ts';
import { updateTileProductType } from '@/store/tiles/slice.ts';
import { getUserPreferences } from '@/store/userPreferences/slice.ts';
import { normalizeEmailsList } from '@/utils/notificationEmails.ts';

import type { AppDispatch, AppState } from '../store';

export function registerTabsEffects(startAppListening: StartAppListening) {
  startAppListening({
    actionCreator: onConfirmRemoveSharedTab,
    effect: async ({ payload: tabId }, { dispatch }) =>
      void dispatch(workspaceApi.endpoints.stopSharing.initiate({ tabId })),
  });
}

export function registerUpdateTileProductTypeListener(startAppListening: StartAppListening) {
  startAppListening({
    actionCreator: updateTileProductType,
    effect: async (action, { getState, dispatch }) => {
      const { tileId, productType, currencyPair, overriddenClientId } = action.payload;

      const state = getState();
      const clientId = overriddenClientId ?? selectTileClientId(state, tileId);

      switch (productType) {
        case 'Cash':
          switchToCash(
            tileId,
            currencyPair,
            { clientId, isOverriddenClientId: isDefined(overriddenClientId) },
            state,
            dispatch,
          );
          break;

        case 'Swap':
          switchToSwap(
            tileId,
            currencyPair,
            { clientId, isOverriddenClientId: isDefined(overriddenClientId) },
            state,
            dispatch,
          );
          break;

        case 'Option':
          switchToOption(
            tileId,
            currencyPair,
            { clientId, isOverriddenClientId: isDefined(overriddenClientId) },
            state,
            dispatch,
          );
          break;

        case 'FlexiForward':
          switchToFlexiForward(
            tileId,
            currencyPair,
            { clientId, isOverriddenClientId: isDefined(overriddenClientId) },
            state,
            dispatch,
          );
          break;

        case 'TakeProfitOrder':
          switchToTakeProfitOrder(
            tileId,
            currencyPair,
            { clientId, isOverriddenClientId: isDefined(overriddenClientId) },
            state,
            dispatch,
          );
          break;

        case 'StopLossOrder':
          switchToStopLossOrder(
            tileId,
            currencyPair,
            { clientId, isOverriddenClientId: isDefined(overriddenClientId) },
            state,
            dispatch,
          );
          break;

        case 'FixingOrder':
          switchToFixingOrder(
            tileId,
            currencyPair,
            { clientId, isOverriddenClientId: isDefined(overriddenClientId) },
            state,
            dispatch,
            getState,
          );
          break;

        case 'CallOrder':
          switchToCallOrder(
            tileId,
            currencyPair,
            { clientId, isOverriddenClientId: isDefined(overriddenClientId) },
            state,
            dispatch,
          );
          break;

        case 'Twap+Order':
          switchToTwapPlusOrder(
            tileId,
            currencyPair,
            { clientId, isOverriddenClientId: isDefined(overriddenClientId) },
            state,
            dispatch,
          );
          break;

        case 'FalconOrder':
          switchToFalconOrder(
            tileId,
            currencyPair,
            { clientId, isOverriddenClientId: isDefined(overriddenClientId) },
            state,
            dispatch,
          );
          break;

        case 'NightjarOrder':
          switchToNightjarOrder(
            tileId,
            currencyPair,
            { clientId, isOverriddenClientId: isDefined(overriddenClientId) },
            state,
            dispatch,
          );
          break;

        case 'TargetAccumulator':
          switchToTargetAccumulator(
            tileId,
            currencyPair,
            { clientId, isOverriddenClientId: isDefined(overriddenClientId) },
            state,
            dispatch,
          );
          break;

        case 'ForwardAccumulator':
          switchToForwardAccumulator(
            tileId,
            currencyPair,
            { clientId, isOverriddenClientId: isDefined(overriddenClientId) },
            state,
            dispatch,
          );
          break;
        default:
          throw new Error('Unknown product type');
      }
    },
  });
}

function switchToCash(
  tileId: string,
  currencyPair: string | undefined,
  clientIdData: { clientId: number; isOverriddenClientId: boolean },
  state: AppState,
  dispatch: AppDispatch,
) {
  const tile = selectCashTile(state, tileId);
  const [currency1, currency2] = currencyPair?.split('/') || [];
  const { data: dataByCurrencyPair } = selectCurrenciesQueryResult(state);
  const { clientId, isOverriddenClientId } = clientIdData;

  if (isOverriddenClientId) {
    dispatch(cashClientIdUpdated({ tileId, clientId, isTabClientOverriden: true }));
  }

  if (
    isNotDefined(currencyPair) ||
    isNotDefined(currency1) ||
    isNotDefined(currency2) ||
    isNotDefined(clientId) ||
    isNotDefined(tile) ||
    isNotDefined(dataByCurrencyPair)
  ) {
    return;
  }

  const notional = tile.properties.notional;
  const notionalCurrencyIndex = tile.properties.notionalCurrencyIndex;
  const maturityDateTenor = tile.properties.maturityDateTenor;
  const maturityDate = tile.properties.maturityDate;
  const currencyPairData = dataByCurrencyPair.dict[currencyPair];
  const isCurrencyPairInverted = currencyPairData?.isInverted ?? false;

  if (
    isCurrencyPairInverted ||
    (isDefined(maturityDateTenor) && maturityDateTenor !== 'SP') ||
    isDefined(maturityDate) ||
    isNonEmpty(maturityDate)
  ) {
    dispatch(
      cashApi.endpoints.checkCashByTradeCapture.initiate({
        tileId,
        type: 'FxFwd',
        properties: {
          currencyPair,
          notional,
          notionalCurrencyIndex,
          maturityDate,
          maturityDateTenor,
        },
      }),
    );
  } else {
    dispatch(
      cashApi.endpoints.checkCashByTradeCapture.initiate({
        tileId,
        type: 'FxSpot',
        properties: {
          currencyPair,
          notional,
          notionalCurrencyIndex,
          maturityDateTenor: 'SP',
        },
      }),
    );
  }
}

function switchToSwap(
  tileId: string,
  currencyPair: string | undefined,
  clientIdData: { clientId: number; isOverriddenClientId: boolean },
  state: AppState,
  dispatch: AppDispatch,
) {
  const tile = selectSwapTile(state, tileId);
  const preferences = getUserPreferences(state);
  const defaultNotional = isDefined(preferences.defaultNotional) ? Number(preferences.defaultNotional) : undefined;

  const { clientId, isOverriddenClientId } = clientIdData;

  if (isOverriddenClientId) {
    dispatch(swapClientIdUpdated({ tileId, clientId, isTabClientOverriden: true }));
  }

  void getSwapTradeCaptureQueue(tileId).add(async () => {
    await dispatch(
      swapApi.endpoints.checkSwapByTradeCapture.initiate({
        tileId,
        isNonDeliverable: false,
        properties: {
          currencyPair: currencyPair,
          nearNotional: isDefined(tile?.properties.nearNotional)
            ? tile.properties.nearNotional
            : (defaultNotional ?? undefined),
          nearNotionalCurrencyIndex: isDefined(tile?.properties.nearNotionalCurrencyIndex)
            ? tile.properties.nearNotionalCurrencyIndex
            : 1,
          farNotional: isDefined(tile?.properties.farNotional)
            ? tile.properties.farNotional
            : (defaultNotional ?? undefined),
          farNotionalCurrencyIndex: isDefined(tile?.properties.farNotionalCurrencyIndex)
            ? tile.properties.farNotionalCurrencyIndex
            : 1,
        },
      }),
    );
  });
}

function switchToFlexiForward(
  tileId: string,
  currencyPair: string | undefined,
  clientIdData: { clientId: number; isOverriddenClientId: boolean },
  state: AppState,
  dispatch: AppDispatch,
) {
  const { clientId, isOverriddenClientId } = clientIdData;

  const flexiForwardTile = selectFlexiForwardTile(state, tileId);

  if (isOverriddenClientId) {
    dispatch(flexiForwardClientChanged({ tileId, clientId, isTabClientOverridden: true }));
  }

  if (isNotDefined(currencyPair) || isNotDefined(clientId) || isNotDefined(flexiForwardTile)) {
    return;
  }

  dispatch(
    initiateCheckFlexiForwardByTradeCapture({
      tileId: tileId,
      properties: {
        currencyPair: currencyPair,
        upfront: 0,
      },
      tradeCapture: flexiForwardTile.tradeCapture,
    }),
  );
}

function switchToOption(
  tileId: string,
  currencyPair: string | undefined,
  clientIdData: { clientId: number; isOverriddenClientId: boolean },
  state: AppState,
  dispatch: AppDispatch,
) {
  const option = optionSlice.selectors.getOptionTile(state, tileId);
  const { clientId, isOverriddenClientId } = clientIdData;

  if (isOverriddenClientId) {
    dispatch(optionSlice.actions.changeClient({ tileId, clientId }));
  }

  if (isNotDefined(currencyPair) || isNotDefined(clientId) || isNotDefined(option)) {
    return;
  }

  dispatch(
    checkOptionThunk({
      type: 'init',
      productName: 'Vanilla',
      tileId,
      shouldApplyUserPrefs: true,
    }),
  );
}

function switchToTakeProfitOrder(
  tileId: string,
  currencyPair: string | undefined,
  clientIdData: { clientId: number; isOverriddenClientId: boolean },
  state: AppState,
  dispatch: AppDispatch,
) {
  const tile = selectTakeProfitTile(state, tileId);
  const [currency1, currency2] = currencyPair?.split('/') || [];
  const { clientId, isOverriddenClientId } = clientIdData;

  if (isOverriddenClientId) {
    dispatch(ordersSlice.actions.changeClientId({ tileId, clientId }));
  }

  if (isNotDefined(currency1) || isNotDefined(currency2) || isNotDefined(clientId) || isNotDefined(tile)) {
    return;
  }

  dispatch(
    takeProfitOrderApi.endpoints.validateTakeProfit.initiate({
      tileId,
      clientId,
      currency1,
      currency2,
      expiry: tile.properties.expiry,
      way: tile.properties.clientWay,
      notional: tile.properties.notional,
      notionalCurrencyIndex: tile.properties.notionalCurrencyIndex,
      notificationEmails: {
        isOverridenEmails: isDefined(tile.properties.notificationEmails),
        emails: normalizeEmailsList(tile.properties.notificationEmails ?? []),
      },
      localTimeWithOffSet: 0,
    }),
  );
}

function switchToStopLossOrder(
  tileId: string,
  currencyPair: string | undefined,
  clientIdData: { clientId: number; isOverriddenClientId: boolean },
  state: AppState,
  dispatch: AppDispatch,
) {
  const tile = selectStopLossTile(state, tileId);
  const [currency1, currency2] = currencyPair?.split('/') || [];
  const { clientId, isOverriddenClientId } = clientIdData;

  if (isOverriddenClientId) {
    dispatch(ordersSlice.actions.changeClientId({ tileId, clientId }));
  }

  if (isNotDefined(currency1) || isNotDefined(currency2) || isNotDefined(clientId) || isNotDefined(tile)) {
    return;
  }

  dispatch(
    stopLossOrderApi.endpoints.validateStopLoss.initiate({
      tileId,
      currency1,
      currency2,
      clientId,
      way: tile.properties.clientWay,
      notional: tile.properties.notional,
      trigger: tile.properties.trigger,
      notionalCurrencyIndex: tile.properties.notionalCurrencyIndex,
      limitPrice: tile.properties.limitPrice,
      expiry: tile.properties.expiry,
      notificationEmails: {
        isOverridenEmails: isDefined(tile.properties.notificationEmails),
        emails: normalizeEmailsList(tile.properties.notificationEmails ?? []),
      },
      clockOffset: tile.properties.clockOffset,
    }),
  );
}

function switchToFixingOrder(
  tileId: string,
  currencyPair: string | undefined,
  clientIdData: { clientId: number; isOverriddenClientId: boolean },
  state: AppState,
  dispatch: AppDispatch,
  getState: () => AppState,
) {
  const tile = selectFixingTile(state, tileId);
  const [currency1, currency2] = currencyPair?.split('/') || [];
  const { clientId, isOverriddenClientId } = clientIdData;

  if (isOverriddenClientId) {
    dispatch(ordersSlice.actions.changeClientId({ tileId, clientId }));
  }

  if (isNotDefined(currency1) || isNotDefined(currency2) || isNotDefined(clientId) || isNotDefined(tile)) {
    return;
  }

  dispatch(
    fixingOrderApi.endpoints.validateFixing.initiate({
      tileId,
      currency1,
      currency2,
      clientId,
      way: tile.properties.clientWay,
      notional: tile.properties.notional,
      notionalCurrencyIndex: tile.properties.notionalCurrencyIndex,
      fixingDateUtc: tile.properties.fixingDateUtc,
      expiryDay: tile.properties.expiryDay,
      fixingReference: tile.properties.fixingReference,
      fixingPriceType: tile.properties.fixingPriceType,
      fixingTime: tile.properties.fixingTime,
      fixingPlace: tile.properties.fixingPlace,
      commercialMargin: tile.properties.commercialMargin,
      fixingMarginType: tile.properties.fixingMarginType,
      notificationEmails: {
        isOverridenEmails: isDefined(tile.properties.notificationEmails),
        emails: normalizeEmailsList(tile.properties.notificationEmails ?? []),
      },
      clockOffset: tile.properties.clockOffset,
    }),
  )
    .unwrap()
    .then((response) => {
      const updatedProperties = selectFixingTile(getState(), tileId);
      if (isNotDefined(updatedProperties)) {
        return;
      }

      const fixingReference = response.fixingTypes.length > 0 ? response.fixingTypes[0] : undefined;
      dispatch(
        fixingOrderApi.endpoints.validateFixing.initiate({
          tileId,
          currency1,
          currency2,
          clientId,
          way: updatedProperties.properties.clientWay,
          notional: updatedProperties.properties.notional,
          notionalCurrencyIndex: updatedProperties.properties.notionalCurrencyIndex ?? 1,
          fixingDateUtc: updatedProperties.properties.fixingDateUtc,
          expiryDay: updatedProperties.properties.expiryDay,
          fixingReference: fixingReference ?? updatedProperties.properties.fixingReference,
          fixingPriceType: updatedProperties.properties.fixingPriceType,
          fixingTime: updatedProperties.properties.fixingTime,
          fixingPlace: updatedProperties.properties.fixingPlace,
          commercialMargin: updatedProperties.properties.commercialMargin,
          fixingMarginType: updatedProperties.properties.fixingMarginType,
          notificationEmails: {
            isOverridenEmails: isDefined(updatedProperties.properties.notificationEmails),
            emails: normalizeEmailsList(updatedProperties.properties.notificationEmails ?? []),
          },
          clockOffset: updatedProperties.properties.clockOffset,
        }),
      );
    });
}

function switchToCallOrder(
  tileId: string,
  currencyPair: string | undefined,
  clientIdData: { clientId: number; isOverriddenClientId: boolean },
  state: AppState,
  dispatch: AppDispatch,
) {
  const tile = selectCallOrderTile(state, tileId);
  const [currency1, currency2] = currencyPair?.split('/') || [];
  const { clientId, isOverriddenClientId } = clientIdData;

  if (isOverriddenClientId) {
    dispatch(ordersSlice.actions.changeClientId({ tileId, clientId }));
  }

  if (isNotDefined(currency1) || isNotDefined(currency2) || isNotDefined(clientId) || isNotDefined(tile)) {
    return;
  }

  dispatch(
    callOrderApi.endpoints.validateCallOrder.initiate({
      tileId,
      currency1,
      currency2,
      clientId,
      limitPrice: tile.properties.limitPrice,
      expiry: tile.properties.expiry,
      notificationEmails: {
        isOverridenEmails: isDefined(tile.properties.notificationEmails),
        emails: normalizeEmailsList(tile.properties.notificationEmails ?? []),
      },
      clockOffset: tile.properties.clockOffset,
    }),
  );
}

function switchToTwapPlusOrder(
  tileId: string,
  currencyPair: string | undefined,
  clientIdData: { clientId: number; isOverriddenClientId: boolean },
  state: AppState,
  dispatch: AppDispatch,
) {
  const dataByCurrencyPair = selectDataByCurrencyPair(state);
  const tile = selectAlgoTile(state, tileId);
  const [currency1, currency2] = currencyPair?.split('/') || [];
  const { clientId, isOverriddenClientId } = clientIdData;

  if (isOverriddenClientId) {
    dispatch(ordersSlice.actions.changeClientId({ tileId, clientId }));
  }

  if (isNotDefined(currency1) || isNotDefined(currency2) || isNotDefined(tile) || tile.type !== 'Twap+Order') {
    return;
  }

  const isNotDeliverableCurrencyPair =
    dataByCurrencyPair.data?.dict?.[`${currency1}/${currency2}`]?.notDeliverable ?? false;
  dispatch(
    algoOrderApi.endpoints.validateAlgo.initiate({
      orderType: 'Twap+Order',
      tileId,
      currency1,
      currency2,
      clientId,
      way: tile.properties.clientWay,
      notional: tile.properties.notional,
      notionalCurrencyIndex: tile.properties.notionalCurrencyIndex,
      limitPrice: tile.properties.limitPrice,
      isNdf: isNotDeliverableCurrencyPair,
      clippingMode: tile.properties.clippingMode,
      spreadCapture: tile.properties.spreadCapture,
      clipSize: tile.properties.clipSize,
      randomize: tile.properties.randomize,
      liquidityPool: tile.properties.liquidityPool,
      tenor: tile.properties.tenor,
      expiryDate: tile.properties.expiryDate,
      startDate: tile.properties.startDate,
      notificationEmails: {
        isOverridenEmails: isDefined(tile.properties.notificationEmails),
        emails: normalizeEmailsList(tile.properties.notificationEmails ?? []),
      },
      clockOffset: tile.properties.clockOffset,
    }),
  );
}

function switchToFalconOrder(
  tileId: string,
  currencyPair: string | undefined,
  clientIdData: { clientId: number; isOverriddenClientId: boolean },
  state: AppState,
  dispatch: AppDispatch,
) {
  const dataByCurrencyPair = selectDataByCurrencyPair(state);
  const tile = selectAlgoTile(state, tileId);
  const [currency1, currency2] = currencyPair?.split('/') || [];
  const { clientId, isOverriddenClientId } = clientIdData;

  if (isOverriddenClientId) {
    dispatch(ordersSlice.actions.changeClientId({ tileId, clientId }));
  }

  if (isNotDefined(currency1) || isNotDefined(currency2) || isNotDefined(tile) || tile.type !== 'FalconOrder') {
    return;
  }
  const isNotDeliverableCurrencyPair =
    dataByCurrencyPair.data?.dict?.[`${currency1}/${currency2}`]?.notDeliverable ?? false;

  dispatch(
    algoOrderApi.endpoints.validateAlgo.initiate({
      orderType: 'FalconOrder',
      tileId,
      currency1,
      currency2,
      clientId,
      way: tile.properties.clientWay,
      notional: tile.properties.notional,
      notionalCurrencyIndex: tile.properties.notionalCurrencyIndex,
      limitPrice: tile.properties.limitPrice,
      isNdf: isNotDeliverableCurrencyPair,
      liquidityPool: tile.properties.liquidityPool,
      tenor: tile.properties.tenor,
      expiryDate: tile.properties.expiryDate,
      startDate: tile.properties.startDate,
      speed: tile.properties.speed,
      notificationEmails: {
        isOverridenEmails: isDefined(tile.properties.notificationEmails),
        emails: normalizeEmailsList(tile.properties.notificationEmails ?? []),
      },
      clockOffset: tile.properties.clockOffset,
    }),
  );
}

function switchToNightjarOrder(
  tileId: string,
  currencyPair: string | undefined,
  clientIdData: { clientId: number; isOverriddenClientId: boolean },
  state: AppState,
  dispatch: AppDispatch,
) {
  const dataByCurrencyPair = selectDataByCurrencyPair(state);
  const tile = selectAlgoTile(state, tileId);
  const [currency1, currency2] = currencyPair?.split('/') || [];
  const { clientId, isOverriddenClientId } = clientIdData;

  if (isOverriddenClientId) {
    dispatch(ordersSlice.actions.changeClientId({ tileId, clientId }));
  }

  if (isNotDefined(currency1) || isNotDefined(currency2) || isNotDefined(tile) || tile.type !== 'NightjarOrder') {
    return;
  }

  const isNotDeliverableCurrencyPair =
    dataByCurrencyPair.data?.dict?.[`${currency1}/${currency2}`]?.notDeliverable ?? false;

  dispatch(
    algoOrderApi.endpoints.validateAlgo.initiate({
      orderType: 'NightjarOrder',
      tileId,
      currency1,
      currency2,
      clientId,
      way: tile.properties.clientWay,
      notional: tile.properties.notional,
      notionalCurrencyIndex: tile.properties.notionalCurrencyIndex,
      limitPrice: tile.properties.limitPrice,
      isNdf: isNotDeliverableCurrencyPair,
      liquidityPool: tile.properties.liquidityPool,
      alphaSeeker: tile.properties.alphaSeeker,
      speed: tile.properties.speed,
      tenor: tile.properties.tenor,
      expiryDate: tile.properties.expiryDate,
      startDate: tile.properties.startDate,
      notificationEmails: {
        isOverridenEmails: isDefined(tile.properties.notificationEmails),
        emails: normalizeEmailsList(tile.properties.notificationEmails ?? []),
      },
      clockOffset: tile.properties.clockOffset,
    }),
  );
}

function switchToTargetAccumulator(
  tileId: string,
  currencyPair: string | undefined,
  clientIdData: { clientId: number; isOverriddenClientId: boolean },
  state: AppState,
  dispatch: AppDispatch,
) {
  const targetAccumulator = targetAccumulatorSlice.selectors.getTargetAccumulator(state, tileId);
  const { clientId, isOverriddenClientId } = clientIdData;

  if (isOverriddenClientId) {
    dispatch(
      targetAccumulatorSlice.actions.targetAccumulatorClientIdUpdated({ tileId, clientId, isTabClientOverriden: true }),
    );
  }

  if (isNotDefined(currencyPair) || isNotDefined(clientId) || isNotDefined(targetAccumulator)) {
    return;
  }

  dispatch(
    targetAccumulatorApi.endpoints.checkTargetAccumulator.initiate({
      tileId: tileId,
      properties: {
        currencyPair: currencyPair,
      },
    }),
  );
}

function switchToForwardAccumulator(
  tileId: string,
  currencyPair: string | undefined,
  clientIdData: { clientId: number; isOverriddenClientId: boolean },
  state: AppState,
  dispatch: AppDispatch,
) {
  const forwardAccumulator = forwardAccumulatorSlice.selectors.getForwardAccumulator(state, tileId);
  const { clientId, isOverriddenClientId } = clientIdData;

  if (isOverriddenClientId) {
    dispatch(
      forwardAccumulatorSlice.actions.forwardAccumulatorClientIdUpdated({
        tileId,
        clientId,
        isTabClientOverriden: true,
      }),
    );
  }

  if (isNotDefined(currencyPair) || isNotDefined(clientId) || isNotDefined(forwardAccumulator)) {
    return;
  }

  dispatch(
    forwardAccumulatorApi.endpoints.checkForwardAccumulator.initiate({
      tileId: tileId,
      properties: {
        currencyPair: currencyPair,
      },
    }),
  );
}
